import {
  addElement,
  defaultGenericElement,
  getNextRetouchLayer,
  reversionElement,
  updateElement,
  type GenericElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { useEditorStore } from '../../stores/useEditorStore';
import { clipToMainImage } from '../../utils/canvas';
import { ensureLoadedImage } from '../../utils/image';
import { distance } from '../../utils/math';
import { translateSvgPathWithSubpaths } from '../../utils/svg';
import { freedraw, type GenericFreedrawElement } from '../freedraw';
import { getFreeDrawElementAbsoluteCoords } from '../freedraw/utils/coords';
import { simplifyPoints } from '../freedraw/utils/points';
import { getFreedrawShape } from '../freedraw/utils/shape';
import { general } from '../general';

import { getPatternScale } from './utils/patternScale';

type Mask = {
  data: string;
  dx: number;
  dy: number;
};

const preloadPatternImage = async (patternName: string) => {
  if (state.loadedPatterns[patternName] != null) return;

  const image = await ensureLoadedImage(patternName);
  if (image) {
    state.loadedPatterns[patternName] = image;
    return;
  }
};

export interface RetouchPatternElement extends GenericFreedrawElement {
  type: 'retouchPattern';
  mask: Mask | null;
  patternName: string;
}

const state: {
  newStrokeWidth: number;
  limitToMask: boolean;
  canvasPattern: CanvasPattern | null;
  patternName: string;
  loadedPatterns: Record<string, HTMLImageElement>;
} = {
  newStrokeWidth: 16,
  limitToMask: true,
  canvasPattern: null,
  patternName: '/editorPatterns/thick-grass.jpg',
  loadedPatterns: {},
};

const actions = {
  activate: () => {
    general.actions.activate(plugin.name, true, false);
  },
  setPattern: async (patternName: string) => {
    await preloadPatternImage(patternName);
    state.patternName = patternName;
  },
  setStrokeWidth: (width: number) => {
    state.newStrokeWidth = width;
  },
  setLimitToMask: (limit: boolean) => {
    state.limitToMask = limit;
  },
  updateColorForElement: (id: string, color: string) => {
    const elements = useEditorStore.getState().elements;
    const element = elements.get(id) as RetouchPatternElement;

    updateElement<RetouchPatternElement>(elements, element, {
      strokeColor: color,
      ...reversionElement(element, ['canvas']),
    });

    renderStaticScene();
  },
};

const plugin: Plugin<RetouchPatternElement> & {
  actions: typeof actions;
  state: typeof state;
} = {
  name: 'retouchPattern',
  cursor: 'crosshair',
  actions,
  state: state,
  events: {
    onPointerDown: ({ event, helpers, runtimeState }) => {
      const { button, pos } = event;

      if (button === 'left' && runtimeState.activeTool === 'retouchPattern') {
        const activeElementId = useEditorStore.getState().selectedElementIds;
        const activeElement = useEditorStore
          .getState()
          .elements.get(activeElementId[0]);

        helpers.updateRuntimeStore({
          newElement: {
            ...defaultGenericElement(),
            layer: getNextRetouchLayer(),
            isLocked: true,
            originX: pos.x,
            originY: pos.y,
            points: [[0, 0]],
            strokeColor: '#000000',
            strokeWidth: state.newStrokeWidth,
            type: 'retouchPattern',
            x: pos.x,
            y: pos.y,
            booleanMode: 'union',
            patternName: state.patternName,
            mask:
              state.limitToMask && activeElement != null
                ? {
                    data:
                      activeElement.type !== 'boolean'
                        ? translateSvgPathWithSubpaths(
                            activeElement.shape.data,
                            -activeElement.x,
                            -activeElement.y,
                          )
                        : activeElement.shape.data,
                    dx: activeElement.x,
                    dy: activeElement.y,
                  }
                : null,
          } as RetouchPatternElement,
        });

        helpers.showNewElementCanvas();
        helpers.renderNewElementScene();
      }
    },
    onPointerMove: ({ event, helpers, runtimeState }) => {
      const { button, pos } = event;
      if (
        button === 'left' &&
        runtimeState.newElement &&
        runtimeState.newElement.type === 'retouchPattern'
      ) {
        const element = runtimeState.newElement as RetouchPatternElement;

        const newElement = {
          ...element,
          ...reversionElement(element, ['bounds', 'canvas']),
          points: [...element.points, [pos.x - element.x, pos.y - element.y]],
        } as GenericElement;

        const [x1, y1, x2, y2] = getFreeDrawElementAbsoluteCoords(
          newElement as RetouchPatternElement,
        );
        const width = distance(x1, x2);
        const height = distance(y1, y2);

        helpers.updateRuntimeStore({
          newElement: { ...newElement, height, width },
        });
        helpers.renderNewElementScene();
      }
    },
    onPointerUp: ({ elementState, helpers, history, runtimeState }) => {
      if (
        !runtimeState.newElement ||
        runtimeState.activeTool !== 'retouchPattern'
      ) {
        return;
      }

      const newElement = runtimeState.newElement as RetouchPatternElement;

      const simplified = simplifyPoints(newElement.points);

      const freedrawElement = {
        ...runtimeState.newElement,
        points: simplified.points,
        bezierHandles: simplified.bezierHandles,
        shape: getFreedrawShape({ ...newElement, ...simplified }),
      };

      addElement(elementState.elements, freedrawElement);
      history.push(elementState);
      updateRuntimeStore({ newElement: undefined });

      helpers.renderStaticScene();
      helpers.hideNewElementCanvas();
    },
    blockTouchPanning: true,
  },
  render: {
    ...freedraw.render,
    draw: ({ config, context, element, runtimeState, elementState }) => {
      if (!runtimeState.newElement && !element.points.length) return;

      if (
        element.renderedById != null ||
        state.loadedPatterns[element.patternName] == null
      )
        return;

      context.save();

      clipToMainImage(
        context,
        elementState.elements,
        element,
        element.strokeWidth / 2,
      );

      if (element.mask != null) {
        const clipPath = new Path2D(
          translateSvgPathWithSubpaths(
            element.mask.data,
            element.mask.dx - element.x + element.strokeWidth / 2,
            element.mask.dy - element.y + element.strokeWidth / 2,
          ),
        );
        context.clip(clipPath);
      }

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      // context.strokeStyle = element.strokeColor;
      context.lineWidth = element.strokeWidth;
      context.lineCap = 'round';

      context.beginPath();

      const points = element.points.map((point) => [
        point[0] + element.strokeWidth / 2,
        point[1] + element.strokeWidth / 2,
      ]);

      context.moveTo(points[0][0], points[0][1]);

      for (let i = 1; i < points.length - 1; i++) {
        const [x1, y1] = points[i];
        const [x2, y2] = points[i + 1];

        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;

        context.quadraticCurveTo(x1, y1, midX, midY);
      }

      const lastPoint = points[element.points.length - 1];
      context.lineTo(lastPoint[0], lastPoint[1]);

      const pattern = context.createPattern(
        state.loadedPatterns[element.patternName],
        'repeat',
      );

      if (!pattern) {
        console.error('Failed to create pattern');
        context.restore();
        return;
      }

      const patternScale = getPatternScale(elementState.elements);
      const transform = new DOMMatrix()
        .scale(patternScale, patternScale)
        .translate(
          -(element.originX - element.strokeWidth / 2) / patternScale,
          -(element.originY - element.strokeWidth / 2) / patternScale,
        );

      pattern.setTransform(transform);

      context.strokeStyle = pattern;

      context.stroke();
      context.restore();
    },
    isRetouchTool: true,
  },
  export: (element) => {
    const prepared = prepareElementForExport(
      element,
    ) as Partial<RetouchPatternElement>;

    prepared.points = element.points.map(([x, y]) => [
      roundExportVal(x),
      roundExportVal(y),
    ]);

    return prepared;
  },
  collision: {
    onlyCountDirectElementHitAfterSelection: false,
    bounds: freedraw.collision.bounds,
    calculateShape: () => ({ data: '', type: 'line' }),
    isPointInShape: () => false,
    isPointOnShape: () => false,
  },
  transformation: {
    shiftBySizeOnFlip: false,
    resizedBounds: () => [0, 0, 0, 0, 0, 0],
  },
  import: async (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as RetouchPatternElement;

    await preloadPatternImage(imported.patternName);

    imported.shape = getFreedrawShape(imported);

    return imported;
  },
};

export const retouchPattern = plugin;
